// External Libraries
import React from "react";
import _ from "lodash";
import * as Icon from "react-feather";
import Modal from "react-responsive-modal";

// Services
import ClientService from "../../../services/ClientService";
import ConfigurationService from "../../../services/ConfigurationService";
import QboService from "../../../services/QboService";
import UserService from "../../../services/UserService";
import UtilityService from "../../../services/UtilityService";

// Common Components
import WComponent from "../../common/WComponent";
import ToolCard from "../../common/tool_card/ToolCard";
import TextFieldInput from "../../common/text_field_input/TextFieldInput";

// Components
import PlaidConnectionModal from "./PlaidConnectionModal";

// Styling
import "./connections.css";

const { alert } = UtilityService;

const CONNECTIONS = {
	plaidConnection: {
		type: "plaidConnection",
		title: "Connect Bank Account",
		description: "Creates a new link to send to a client and have them connect their bank account to Workflow.",
		Icon: Icon.Key
	},
	qboConnection: {
		type: "qboConnection",
		title: "QBO Connection",
		description: "Allows tools like HST Reconciliation to work",
		Icon: Icon.RefreshCcw
	}
};

class Connections extends WComponent {
	constructor(props) {
		super(props);

		this.state = {
			isQBOConnected: false,

			showPlaidConnectionModal: false,

			qboCompanies: [],
			selectedRealmId: "",
			isLoadingCompanies: false,
			isMappingClient: false,
			mapClientSuccess: false,

			driveFolder: null,
			clientName: null,
			showDriveSearchModal: false,
			driveSearchResults: [],
			driveSearchLoading: false,
			selectedDriveFolder: null,
			mapFolderUrlInput: "",
			isRenamingFolder: false,
			showRenameConfirm: false,
			showUnlinkConfirm: false,
			isUnlinkingFolder: false,
			isLinkingFolder: false,
			driveSearchQuery: "",
			driveSearchHasMore: false
		};
	}

	async componentDidMount() {
		let { clientId } = this.props;

		let { data } = await ClientService.getClient({ clientId });

		let isQBOConnected = data.client.qbo_company_id !== null;

		this.update({ isQBOConnected, clientName: data.client.name });

		await this.fetchQboCompanies();
		await this.fetchDriveFolder();
	}

	async fetchQboCompanies() {
		let { clientId } = this.props;
		this.update({ isLoadingCompanies: true });
		try {
			const response = await QboService.getCompanies();
			const companies = response && response.data ? response.data.companies : [];
			const mapped = companies && companies.find(c => String(c.workflowClientId) === String(clientId));
			this.update({ qboCompanies: companies || [], selectedRealmId: mapped ? mapped.realmId : "" });
		} catch (error) {
			console.log(error);
		}
		this.update({ isLoadingCompanies: false });
	}

	fetchDriveFolder = async () => {
		let { clientId } = this.props;
		const driveFolder = await ClientService.getDriveFolder({ clientId });
		this.update({ driveFolder: driveFolder ?? null });
	};

	onSeeFiles = () => {
		const { driveFolder } = this.state;

		if (driveFolder) {
			window.open(driveFolder.drive_folder_url, "_blank");
			return;
		}

		// Only administrators can link a folder. Non-admins have nothing to open yet.
		if (!UserService.isAdministrator()) {
			alert({ type: "info", text: "No Google Drive folder has been linked yet. Please ask an administrator to link one." });
			return;
		}

		this.onOpenDriveSearchModal();
	};

	onOpenDriveSearchModal = async () => {
		let { clientId } = this.props;
		const { driveFolder, clientName } = this.state;
		const initialQuery = clientName || "";

		this.update({
			showDriveSearchModal: true,
			driveSearchResults: [],
			driveSearchLoading: true,
			driveSearchQuery: initialQuery,
			driveSearchHasMore: false,
			selectedDriveFolder: null,
			mapFolderUrlInput: driveFolder ? driveFolder.drive_folder_url : ""
		});

		const response = await ClientService.searchDriveFolders({ clientId });

		if (!response) {
			this.update({ driveSearchLoading: false });
			alert({ type: "error", text: "Failed to load Drive folders. Please try again." });
			return;
		}

		const results = response.results || [];
		const currentFolderName = response.currentFolderName;

		// If Drive has a updated name, match the local state for the mismatch banner to reflect it
		let updatedDriveFolder = driveFolder;
		if (driveFolder && currentFolderName && currentFolderName !== driveFolder.name) {
			updatedDriveFolder = { ...driveFolder, name: currentFolderName };
		}

		let preSelected = null;
		if (updatedDriveFolder && results.length > 0) {
			const currentFolderId = updatedDriveFolder.drive_folder_url
				? (updatedDriveFolder.drive_folder_url.match(/\/folders\/([a-zA-Z0-9_-]+)/)?.[1])
				: null;
			preSelected = currentFolderId ? (results.find(f => f.id === currentFolderId) || null) : null;
		}

		this.update({
			driveFolder: updatedDriveFolder,
			driveSearchResults: results,
			driveSearchLoading: false,
			driveSearchHasMore: !!(response && response.nextPageToken),
			selectedDriveFolder: preSelected,
			mapFolderUrlInput: preSelected ? "" : (updatedDriveFolder ? updatedDriveFolder.drive_folder_url : "")
		});
	};

	onCloseDriveSearchModal = () => {
		this.runDriveSearchDebounced.cancel();
		this.update({
			showDriveSearchModal: false,
			driveSearchResults: [],
			driveSearchLoading: false,
			driveSearchQuery: "",
			driveSearchHasMore: false,
			selectedDriveFolder: null,
			mapFolderUrlInput: "",
			showUnlinkConfirm: false
		});
	};

	onLinkDriveFolder = async () => {
		let { clientId } = this.props;
		const { selectedDriveFolder, mapFolderUrlInput } = this.state;

		let driveFolderId, driveFolderUrl, driveFolderName;

		if (selectedDriveFolder) {
			driveFolderId = selectedDriveFolder.id;
			driveFolderUrl = selectedDriveFolder.url;
			driveFolderName = selectedDriveFolder.name;
		} else if (mapFolderUrlInput.trim()) {
			// Extract the folder ID
			const match = mapFolderUrlInput.match(/\/folders\/([a-zA-Z0-9_-]+)/);
			if (!match) {
				alert({ type: "error", text: "Invalid Google Drive folder URL. Please paste the full folder link." });
				return;
			}
			driveFolderId = match[1];
			driveFolderUrl = `https://drive.google.com/drive/folders/${driveFolderId}`;
			driveFolderName = null;
		} else {
			alert({ type: "error", text: "Please select a folder from the list or paste a folder URL." });
			return;
		}

		this.update({ isLinkingFolder: true });
		const result = await ClientService.mapDriveFolder({ clientId, driveFolderId, driveFolderUrl, driveFolderName });
		this.update({ isLinkingFolder: false });

		if (result) {
			// result.name is now populated (from DB) — no extra fetch needed
			this.update({ driveFolder: result });
			alert({ type: "success", text: "Google Drive folder linked successfully." });
			window.open(driveFolderUrl, "_blank");
			this.onCloseDriveSearchModal();
		} else {
			alert({ type: "error", text: "Failed to link Drive folder. Please try again." });
		}
	};

	onUnlinkDriveFolder = async () => {
		let { clientId } = this.props;
		this.update({ isUnlinkingFolder: true });
		const success = await ClientService.unlinkDriveFolder({ clientId });
		this.update({ isUnlinkingFolder: false });
		if (success) {
			this.update({ driveFolder: null });
			alert({ type: "success", text: "Folder unlinked. The Drive folder was not deleted." });
			this.onCloseDriveSearchModal();
		} else {
			alert({ type: "error", text: "Failed to unlink Drive folder. Please try again." });
		}
	};

	runDriveSearchDebounced = _.debounce(async (query) => {
		let { clientId } = this.props;
		this.update({ driveSearchLoading: true });
		const response = await ClientService.searchDriveFolders({ clientId, query });
		const results = (response && response.results) || [];
		this.update({
			driveSearchResults: results,
			driveSearchLoading: false,
			driveSearchHasMore: !!(response && response.nextPageToken),
		});
	}, 400);

	onRenameDriveFolder = async () => {
		let { clientId } = this.props;
		this.update({ isRenamingFolder: true, showRenameConfirm: false });
		const result = await ClientService.renameDriveFolder({ clientId });
		if (result) {
			this.update({ driveFolder: result, isRenamingFolder: false });
			alert({ type: "success", text: "Drive folder renamed successfully." });
		} else {
			alert({ type: "error", text: "Failed to rename Drive folder. Ensure the app has write access to it." });
			this.update({ isRenamingFolder: false });
		}
	};

	onConnect = async type => {
		if (type === CONNECTIONS.qboConnection.type) {
			const user = UserService.getUserData();
			let tokenParam = "";
			if (user && user.authToken) {
				tokenParam = `?authToken=${encodeURIComponent(user.authToken)}`;
			}
			window.open(`${ConfigurationService.resolveApi().rest}/integration/qbo/auth${tokenParam}`, "_blank");
			return;
		}

		if (type === CONNECTIONS.plaidConnection.type) {
			await this.update({
				showPlaidConnectionModal: true
			});
			return;
		}
	};

	onPlaidConnectionModalClose = () => {
		this.update({
			showPlaidConnectionModal: false
		});
	};

	onCompanyChange = e => {
		this.update({ selectedRealmId: e.target.value, mapClientSuccess: false });
	};

	onMapClient = async () => {
		let { clientId } = this.props;
		let { selectedRealmId } = this.state;

		if (!selectedRealmId) return;

		this.update({ isMappingClient: true, mapClientSuccess: false });
		try {
			await QboService.mapClient({ workflowClientId: clientId, realmId: selectedRealmId });
			this.update({ mapClientSuccess: true });
		} catch (error) {
			console.log(error);
		}
		this.update({ isMappingClient: false });
	};

	renderConnectionOption(toolOption) {
		return <ToolCard toolProps={toolOption} disabled={false} onClick={this.onConnect} />;
	}

	renderQboMapping() {
		let { qboCompanies, selectedRealmId, isLoadingCompanies, isMappingClient, mapClientSuccess } = this.state;
		const user = UserService.getUserData();

		if (isLoadingCompanies) {
			return (
				<div className="qbo-mapping">
					<p className="qbo-mapping__loading">Loading QBO companies...</p>
				</div>
			);
		}

		if (!qboCompanies || qboCompanies.length === 0) {
			return (
				<div className="qbo-mapping">
					<p className="qbo-mapping__label">No QBO companies authorized yet.</p>
					<button
						className="qbo-mapping__authorize-btn"
						onClick={() => {
							let tokenParam = user && user.authToken ? `?authToken=${encodeURIComponent(user.authToken)}` : "";
							window.open(`${ConfigurationService.resolveApi().rest}/integration/qbo/auth${tokenParam}`, "_blank");
						}}
					>
						<Icon.Lock size={14} style={{ marginRight: 6 }} />
						Authorize QBO
					</button>
				</div>
			);
		}

		return (
			<div className="qbo-mapping">
				<p className="qbo-mapping__title">Map Client to QBO</p>
				<div className="qbo-mapping__row">
					<div className="qbo-mapping__field">
						<label className="qbo-mapping__label">Company</label>
						<select className="qbo-mapping__select" value={selectedRealmId} onChange={this.onCompanyChange}>
							<option value="">Select a company</option>
							{qboCompanies.map(c => (
								<option key={c.realmId} value={c.realmId}>
									{c.companyName}
								</option>
							))}
						</select>
					</div>

					<button
						className="qbo-mapping__map-btn"
						onClick={this.onMapClient}
						disabled={!selectedRealmId || isMappingClient}
					>
						{isMappingClient ? "Saving..." : "Map Client"}
					</button>
				</div>
				{mapClientSuccess && <p className="qbo-mapping__success">Client mapped successfully.</p>}
			</div>
		);
	}

	renderDriveConnection() {
		const { driveFolder, clientName, isRenamingFolder, showRenameConfirm } = this.state;
		const isAdmin = UserService.isAdministrator();

		const namesMismatch =
			driveFolder &&
			clientName &&
			driveFolder.name &&
			driveFolder.name.trim().toLowerCase() !== clientName.trim().toLowerCase();

		return (
			<>
				<div className="drive-connection">
					<div className="drive-connection__header">
						<Icon.Folder size={36} className="drive-connection__icon" />
						<div className="drive-connection__text">
							<div className="drive-connection__title">Google Drive</div>
							<div className="drive-connection__desc">
								{driveFolder ? "Folder linked — click See Files to open." : "No folder linked yet."}
							</div>
						</div>
					</div>
					<div className="gdrive-btn">
						<div className="gdrive-btn__main" onClick={this.onSeeFiles}>
							<Icon.Folder size={14} />
							<span>See Files</span>
						</div>
						{driveFolder && isAdmin && (
							<div className="gdrive-btn__settings" onClick={this.onOpenDriveSearchModal} title="Change linked folder">
								<Icon.Settings size={14} />
							</div>
						)}
					</div>
				</div>
				{namesMismatch && isAdmin && !showRenameConfirm && (
					<div className="drive-name-mismatch">
						<Icon.AlertTriangle size={14} className="drive-name-mismatch__icon" />
						<span className="drive-name-mismatch__text">
							Drive folder is named <strong>"{driveFolder.name}"</strong> — doesn't match{" "}
							<strong>"{clientName}"</strong>
						</span>
						<button
							className="drive-name-mismatch__btn"
							onClick={() => this.update({ showRenameConfirm: true })}
							disabled={isRenamingFolder}
						>
							Rename to match
						</button>
					</div>
				)}
				{showRenameConfirm && (
					<div className="drive-rename-confirm">
						<span className="drive-rename-confirm__text">
							Rename Drive folder to <strong>"{clientName}"</strong>?
						</span>
						<div className="drive-rename-confirm__actions">
							<button
								className="drive-rename-confirm__cancel"
								onClick={() => this.update({ showRenameConfirm: false })}
								disabled={isRenamingFolder}
							>
								Cancel
							</button>
							<button
								className="drive-rename-confirm__ok"
								onClick={this.onRenameDriveFolder}
								disabled={isRenamingFolder}
							>
								{isRenamingFolder ? "Renaming…" : "Yes, rename"}
							</button>
						</div>
					</div>
				)}
			</>
		);
	}

	render() {
		let { clientId } = this.props;
		let {
			showPlaidConnectionModal,
			showDriveSearchModal,
			driveSearchResults,
			driveSearchLoading,
			driveSearchQuery,
			driveSearchHasMore,
			selectedDriveFolder,
			mapFolderUrlInput,
			driveFolder,
			showUnlinkConfirm,
			isUnlinkingFolder,
			isLinkingFolder
		} = this.state;

		const connectionsOptionArray = Object.values(CONNECTIONS);

		return (
			<>
				<div className="container">
					<div className="container__body">
						<div className="connections">
							{connectionsOptionArray.map(connectionOption => this.renderConnectionOption(connectionOption))}
							{this.renderDriveConnection()}
							{this.renderQboMapping()}
						</div>
					</div>
				</div>

				<PlaidConnectionModal clientId={clientId} show={showPlaidConnectionModal} onClose={this.onPlaidConnectionModalClose} />

				<Modal open={showDriveSearchModal} onClose={this.onCloseDriveSearchModal} center>
					<div className="modal-title">Link Google Drive Folder</div>
					<div className="modal-content">

						<TextFieldInput
							placeholder="Search Drive folders..."
							value={driveSearchQuery}
							onChange={e => {
								const q = e.target.value;
								this.update({ driveSearchQuery: q, selectedDriveFolder: null });
								if (q.length >= 3) {
									this.runDriveSearchDebounced(q);
								} else {
									this.runDriveSearchDebounced.cancel();
									this.update({ driveSearchResults: [], driveSearchHasMore: false });
								}
							}}
							autoComplete="off"
						/>

						<div className="drive-search__area">
							{driveSearchLoading && (
								<div className="drive-search__status">Searching Drive...</div>
							)}

							{!driveSearchLoading && driveSearchResults.length > 0 && (
								<>
									<div className="drive-search__results">
										{driveSearchResults.map(folder => (
											<div
												key={folder.id}
												className={`drive-search__result ${selectedDriveFolder && selectedDriveFolder.id === folder.id ? "drive-search__result--selected" : ""}`}
												onClick={() => this.update({ selectedDriveFolder: folder, mapFolderUrlInput: "" })}
											>
												<Icon.Folder size={16} />
												<span>{folder.name}</span>
											</div>
										))}
									</div>
									{driveSearchHasMore && (
										<div className="drive-search__name-hint">Showing first 50 results — type more to narrow down.</div>
									)}
								</>
							)}

							{!driveSearchLoading && driveSearchResults.length === 0 && driveSearchQuery.length >= 3 && (
								<div className="drive-search__status">No matching folders found in Drive.</div>
							)}

							{!driveSearchLoading && driveSearchQuery.length > 0 && driveSearchQuery.length < 3 && (
								<div className="drive-search__status">Type at least 3 characters to search.</div>
							)}
						</div>

						<div className="drive-search__divider">Or paste folder URL manually</div>

						<TextFieldInput
							placeholder="https://drive.google.com/drive/folders/..."
							name="mapFolderUrlInput"
							onChange={e => this.update({ mapFolderUrlInput: e.target.value, selectedDriveFolder: null })}
							value={mapFolderUrlInput}
							autoComplete="off"
						/>

						</div>
					{showUnlinkConfirm && (
						<div className="drive-unlink-confirm">
							<span>Remove this folder link? The Drive folder won't be deleted.</span>
							<div className="drive-unlink-confirm__actions">
								<button className="drive-unlink-confirm__cancel" onClick={() => this.update({ showUnlinkConfirm: false })} disabled={isUnlinkingFolder}>
									Cancel
								</button>
								<button className="drive-unlink-confirm__ok" onClick={this.onUnlinkDriveFolder} disabled={isUnlinkingFolder}>
									{isUnlinkingFolder ? "Unlinking…" : "Yes, unlink"}
								</button>
							</div>
						</div>
					)}

					<div className="modal-footer drive-folder-modal-footer">
						{driveFolder && !showUnlinkConfirm && (
							<button className="drive-unlink-btn" onClick={() => this.update({ showUnlinkConfirm: true })}>
								<Icon.Trash2 size={13} />
								Unlink folder
							</button>
						)}
						<div className="modal-footer__actions">
							<button className="qbo-mapping__map-btn qbo-mapping__map-btn--secondary" onClick={this.onCloseDriveSearchModal}>
								Cancel
							</button>
							<button className="qbo-mapping__map-btn" onClick={this.onLinkDriveFolder} disabled={isLinkingFolder}>
								{isLinkingFolder ? "Linking…" : "Link Folder"}
							</button>
						</div>
					</div>
				</Modal>
			</>
		);
	}
}

export default Connections;
